<template>
  <div class="CustomerPortraitsDownload">
    <div class="CustomerPortraitsDownload__wrapper">

      <a
        class="CustomerPortraitsDownload__logo"
        href="https://theartisan.ai"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img class="CustomerPortraitsDownload__logo-img"
          :src="LOGO"
          alt="Powered by TheArtisan.ai">
      </a>

      <img
        class="CustomerPortraitsDownload__cover"
        data-testid="customer-download-cover"
        :src="COVER"
        alt=""
      >
      <h1 class="CustomerPortraitsDownload__title">{{ $t('CustomerPortraitsDownload.title') }}</h1>

      <form class="CustomerPortraitsDownload__form" data-testid="customer-download-form" @submit="onSubmit">
        <div class="CustomerPortraitsDownload__form-group">
          <input class="CustomerPortraitsDownload__form-input ym-record-keys"
            data-testid="customer-download-input"
            type="text"
            placeholder="XXXX−XXXX"
            maxlength="9"
            autocomplete="one-time-code"
            autocapitalize="characters"
            :aria-label="$t('CustomerPortraitsDownload.title')"
            :aria-invalid="Boolean(errorMessage)"
            aria-describedby="customer-download-error"
            :value="code"
            :disabled="isCodeLoading"
            @input="onInput"
            @focus="resetValidation">
          <button class="CustomerPortraitsDownload__form-button DownloadPortraits__button"
            data-testid="customer-download-submit"
            :class="{ 'DownloadPortraits__button--loading': isCodeLoading }"
            :disabled="isCodeLoading"
            type="submit">
            <span v-if="!isCodeLoading">{{ $t('CustomerPortraitsDownload.buttonSubmit') }}</span>
            <span v-else class="CustomerPortraitsDownload__spinner"></span>
          </button>
        </div>

        <div
          v-if="errorMessage"
          id="customer-download-error"
          class="CustomerPortraitsDownload__form-error"
          data-testid="customer-download-error"
          role="alert"
        >
          {{ errorMessage }}
        </div>
      </form>

    </div>
  </div>
</template>


<script>
import { initializeForVisitor } from '@/app/i18n';
import { fetchShared } from '@/app/query-client';
import { router } from '@/app/router';
import { portalSessionQuery } from '@/services/api/endpoints/customer_download';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track, warnMessageOf } from '@/shared/utils/track-telemetry';
import COVER from '../../assets/images/download-code.svg';
import LOGO from '../../assets/images/powered-by-theartisan.svg';
import { useCustomerDownloadDocumentLayout } from './useCustomerDownloadDocumentLayout';

const CODE_ERRORS = {
  WRONG_FORMAT: 'WRONG_FORMAT',
  NOT_FOUND: 'NOT_FOUND',
  API_ERROR: 'API_ERROR',
};

export default {
  setup() {
    useCustomerDownloadDocumentLayout();
  },
  data() {
    return {
      LOGO,
      COVER,
      code: '',
      codeError: null, // 'WRONG_FORMAT' | 'NOT_FOUND' | 'API_ERROR' | null
      isCodeLoading: false,
      // Guard against post-unmount state mutations from in-flight API calls
      _isUnmounted: false,
    };
  },
  computed: {
    errorMessage() {
      if (!this.codeError) return null;
      // Mapping code → specific error key; missing specific → generic.
      const specificKey = {
        [CODE_ERRORS.WRONG_FORMAT]:
          'CustomerPortraitsDownload.errors.wrongFormat',
        [CODE_ERRORS.NOT_FOUND]: 'CustomerPortraitsDownload.errors.notFound',
        [CODE_ERRORS.API_ERROR]: 'CustomerPortraitsDownload.errors.serverError',
      }[this.codeError];
      if (specificKey && this.$te(specificKey)) return this.$t(specificKey);
      return this.$t('CustomerPortraitsDownload.error');
    },
  },
  mounted() {
    this._isUnmounted = false;

    sentryService.addBreadcrumb(
      'customer',
      'CustomerPortraitsDownload mounted'
    );

    exposeToGlobalScope(this);

    initializeForVisitor();
  },
  beforeUnmount() {
    this._isUnmounted = true;
  },
  methods: {
    onInput(e) {
      this.resetValidation();

      // Strip everything except alphanumeric chars (a-z, 0-9)
      // \W misses underscores, so use explicit allowlist instead
      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
      let formattedValue = '';

      for (let i = 0; i < value.length; i++) {
        if (i === 4) {
          formattedValue += '\u2212'; // Unicode minus sign for display
        }
        formattedValue += value[i];
      }

      this.code = formattedValue.slice(0, 9);
    },

    async onSubmit(e) {
      e.preventDefault();

      // Early return if already loading - prevents double submits
      if (this.isCodeLoading) return;
      // Guard against unmounted state
      if (this._isUnmounted) return;

      // CRITICAL: Set loading state IMMEDIATELY after early return check
      // to prevent race condition from double-clicks
      this.isCodeLoading = true;

      // Track whether we successfully navigated (to handle finally block correctly)
      let navigationSucceeded = false;

      try {
        // Sentry: Breadcrumb for form submission start
        sentryService.addBreadcrumb(
          'customer',
          'CustomerPortraitsDownload form submit attempted',
          { code_length: this.code.length }
        );

        // Client-side format validation first
        if (!this.isValidFormat()) {
          // Sentry: Breadcrumb for format validation failure
          sentryService.addBreadcrumb(
            'customer',
            'CustomerPortraitsDownload format validation failed',
            { code_length: this.code.length, expected_length: 8 }
          );
          sentryService.setContext('download_attempt', {
            code_length: this.code.length,
            code_format_valid: false,
            error_type: CODE_ERRORS.WRONG_FORMAT,
          });
          this.codeError = CODE_ERRORS.WRONG_FORMAT;
          return;
        }

        // Sentry: Breadcrumb for format validation success
        sentryService.addBreadcrumb(
          'customer',
          'CustomerPortraitsDownload format validation passed'
        );

        // Server-side validation - check if code exists
        const normalizedCode = this.getNormalizedCode();

        // Sentry: Set context for the download attempt
        sentryService.setContext('download_attempt', {
          code_length: normalizedCode.length,
          code_format_valid: true,
        });

        // Sentry: Start span for API validation call
        let apiSpan = null;
        track(
          '[CustomerPortraitsDownload] Sentry span start failed:',
          () => {
            apiSpan = sentryService.startSpan(
              'download.validate_code',
              'http.client',
              {
                'code.length': normalizedCode.length,
              }
            );
          },
          warnMessageOf
        );

        try {
          // Sentry: Breadcrumb for API validation start
          sentryService.addBreadcrumb(
            'customer',
            'CustomerPortraitsDownload API validation started',
            { code_length: normalizedCode.length }
          );

          // The code page takes this answer from the cache.
          await fetchShared(portalSessionQuery(normalizedCode));

          // Guard: don't proceed with navigation if unmounted during API call
          if (this._isUnmounted) return;

          // Sentry: Mark span as successful
          track(
            '[CustomerPortraitsDownload] Sentry span status failed:',
            () => {
              if (apiSpan) {
                apiSpan.setStatus({ code: 1 }); // OK
              }
            },
            warnMessageOf
          );

          // Sentry: Breadcrumb for API validation success
          sentryService.addBreadcrumb(
            'customer',
            'CustomerPortraitsDownload API validation success',
            { code_length: normalizedCode.length }
          );
        } catch (apiError) {
          // Sentry: Mark span as errored
          track(
            '[CustomerPortraitsDownload] Sentry span status failed:',
            () => {
              if (apiSpan) {
                apiSpan.setStatus({ code: 2, message: apiError.message }); // ERROR
              }
            },
            warnMessageOf
          );

          // Guard: don't update state if unmounted during API call
          if (this._isUnmounted) return;

          // Determine error type from response (uses .context not .cause)
          const responseType = apiError?.context?.response?.json?.type;
          const httpStatus = apiError?.context?.response?.status?.code;

          if (responseType === 'not_found' || httpStatus === 404) {
            // Sentry: Breadcrumb and context for not found error
            sentryService.addBreadcrumb(
              'customer',
              'CustomerPortraitsDownload code not found',
              { code_length: normalizedCode.length, http_status: httpStatus }
            );
            sentryService.setContext('download_attempt', {
              code_length: normalizedCode.length,
              code_format_valid: true,
              error_type: CODE_ERRORS.NOT_FOUND,
              http_status: httpStatus,
            });
            this.codeError = CODE_ERRORS.NOT_FOUND;
          } else {
            // Sentry: Breadcrumb, context, and exception capture for API errors
            sentryService.addBreadcrumb(
              'customer',
              'CustomerPortraitsDownload API error',
              { error_type: responseType, http_status: httpStatus }
            );
            sentryService.setContext('download_attempt', {
              code_length: normalizedCode.length,
              code_format_valid: true,
              error_type: CODE_ERRORS.API_ERROR,
              response_type: responseType,
              http_status: httpStatus,
            });
            // Capture non-404 errors as exceptions for investigation
            sentryService.captureException(apiError, {
              tags: {
                component: 'CustomerPortraitsDownload',
                error_type: CODE_ERRORS.API_ERROR,
              },
              extra: {
                code_length: normalizedCode.length,
                response_type: responseType,
                http_status: httpStatus,
              },
            });
            this.codeError = CODE_ERRORS.API_ERROR;
          }
          return; // Exit early on API error
        } finally {
          // Sentry: Always end the span
          track(
            '[CustomerPortraitsDownload] Sentry span end failed:',
            () => {
              if (apiSpan) {
                apiSpan.end();
              }
            },
            warnMessageOf
          );
        }

        // Sentry: Breadcrumb for navigation initiation
        sentryService.addBreadcrumb(
          'customer',
          'CustomerPortraitsDownload navigation initiated',
          { destination: `/customer/portraits-download/${this.code}` }
        );

        // Navigate to download page - wrap in try/catch to handle navigation failures
        try {
          router.redirectToPath(`/customer/portraits-download/${this.code}`);
          navigationSucceeded = true;
        } catch (navError) {
          // Sentry: Capture navigation failure
          sentryService.addBreadcrumb(
            'customer',
            'CustomerPortraitsDownload navigation failed',
            { error: navError.message }
          );
          sentryService.captureException(navError, {
            tags: {
              component: 'CustomerPortraitsDownload',
              error_type: 'navigation_failure',
            },
            extra: {
              destination: `/customer/portraits-download/${this.code}`,
            },
          });
          // Re-throw so the finally block handles loading state correctly
          throw navError;
        }
      } finally {
        // Reset loading state only if navigation didn't succeed
        // (successful navigation leaves the page, so we don't need to reset)
        if (!navigationSucceeded) {
          this.isCodeLoading = false;
        }
      }
    },

    getNormalizedCode() {
      // Remove all types of dashes: Unicode minus (U+2212), en-dash (U+2013), em-dash (U+2014), regular hyphen (U+002D)
      return this.code
        .trim()
        .replace(/[\u2212\u2013\u2014-]/g, '')
        .toLowerCase();
    },

    isValidFormat() {
      return this.getNormalizedCode().length === 8;
    },

    resetValidation() {
      this.codeError = null;
    },
  },
};
</script>


<style scoped lang="scss">
  .CustomerPortraitsDownload {
    --CustomerPortraitsDownload--max-width: min(
      100vw,
      clamp(360px, 59.5dvh, 550px)
    );
    --CustomerPortraitsDownload--font-size: calc(var(--CustomerPortraitsDownload--max-width) * 0.046);
    --CustomerPortraitsDownload--form-radius: calc(var(--CustomerPortraitsDownload--max-width) * 0.017);
    --CustomerPortraitsDownload--background-height: calc(var(--CustomerPortraitsDownload--max-width) * 1.115);

    background: linear-gradient(
        to bottom,
        #EEF2F6 0,
        #EEF2F6 var(--CustomerPortraitsDownload--background-height),
        #FFFFFF var(--CustomerPortraitsDownload--background-height),
        #FFFFFF 100%
    );
    min-height: 100vh;
    min-height: 100dvh;
  }

  .CustomerPortraitsDownload__wrapper {
    font-size: var(--CustomerPortraitsDownload--font-size);
    max-width: var(--CustomerPortraitsDownload--max-width);
    margin: 0 auto;
    padding:
      calc(var(--CustomerPortraitsDownload--max-width) * .074)
      calc(var(--CustomerPortraitsDownload--max-width) * .074)
      calc(var(--CustomerPortraitsDownload--max-width) * .15)
      calc(var(--CustomerPortraitsDownload--max-width) * .074)
  }

  .CustomerPortraitsDownload__logo-img {
    width: calc(var(--CustomerPortraitsDownload--max-width) * 0.4);
  }

  .CustomerPortraitsDownload__cover {
    width: calc(var(--CustomerPortraitsDownload--max-width) * 0.7);
    display: block;
    margin:
      calc(var(--CustomerPortraitsDownload--max-width) * 0.1)
      auto
      calc(var(--CustomerPortraitsDownload--max-width) * 0.11)
      auto;
  }

  .CustomerPortraitsDownload__title {
    font-size: calc(var(--CustomerPortraitsDownload--max-width) * 0.054);
    font-weight: 500;
    font-variation-settings: 'wght' 500;
    text-align: center;
  }

  .CustomerPortraitsDownload__form-group {
    display: flex;
    align-items: stretch;
    height: calc(var(--CustomerPortraitsDownload--max-width) * 0.125);
    margin-top: calc(var(--CustomerPortraitsDownload--max-width) * 0.042);
  }

  .CustomerPortraitsDownload__form-input {
    display: flex;
    align-items: stretch;
    width: 50%;
    border: 1px solid #BCBEC0;
    box-shadow:
      inset
      0
      calc(var(--CustomerPortraitsDownload--max-width) * 0.007)
      calc(var(--CustomerPortraitsDownload--max-width) * 0.01) 0 rgba(#000000, 0.06);

    border-radius:
      var(--CustomerPortraitsDownload--form-radius)
      0
      0
      var(--CustomerPortraitsDownload--form-radius);

    padding: 0.2em 0 0 0;
    margin: 0;
    background: none;
    appearance: none;
    text-align: center;
    font-size: var(--CustomerPortraitsDownload--font-size);
    text-transform: uppercase;

    &:active,
    &:focus {
      outline: none;
    }
  }

  .CustomerPortraitsDownload__form-button.DownloadPortraits__button {
    width: 50%;
    font-size: var(--CustomerPortraitsDownload--font-size);
    margin-left: -1px;
    border-radius:
      0
      var(--CustomerPortraitsDownload--form-radius)
      var(--CustomerPortraitsDownload--form-radius)
      0;
  }

  .CustomerPortraitsDownload__form-loading {
    --kiosk-spinner-color: #FFFFFF;

    position: absolute;
    top: 50%;
    left: 50%;
  }

  .CustomerPortraitsDownload__form-error {
    margin-top: calc(var(--CustomerPortraitsDownload--max-width) * 0.042);
    text-align: center;
    color: #FA5F42;
  }

  .DownloadPortraits__button {
    font-weight: 500;
    font-variation-settings: 'wght' 500;
    cursor: pointer;
    position: relative;
    background: #0D6EFD;
    color: #FFFFFF;
    border: 0;
    border-radius: 0.4em;
    padding: 0.55em 0.5em 0.5em 0.5em;
    font-size: 100%;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &--loading {
      pointer-events: none;
      opacity: 0.8;
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  .CustomerPortraitsDownload__spinner {
    display: inline-block;
    width: 1.2em;
    height: 1.2em;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #FFFFFF;
    border-radius: 50%;
    animation: CustomerPortraitsDownload__spin 0.8s linear infinite;
  }

  @keyframes CustomerPortraitsDownload__spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
